import { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { DeleteOutline, Edit, Visibility, Block, PowerSettingsNew } from "@mui/icons-material";
import { Chip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";

import { TableWrapperStyle } from "./style.ts";
import headCells from "./tableHeadCells";
import { Order } from "@/utils/table-utils";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";
import RevokeModal from "@/components/app/modalVariants/revoke";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { useDeleteCoupon, useRevokeCoupon, useToggleCoupon } from "@/api/hooks/useCoupons";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import TableSkeleton from "@/components/app/TableSkeleton";
import Loading from "@/components/app/Loading/tableLoading";
import { ICoupon } from "@/api/types/coupons";
import { getAxiosError } from "@/utils/get-axios-error";

export interface ICouponsTable {
    list: ICoupon[];
    totalItems: number;
    rowsPerPage: number;
    page: number;
    isFetching: boolean;
    isLoading: boolean;
    onChange: (data: { page?: number, rowsPerPage?: number, sort?: string, filter?: any }) => void;
}

export default function CouponsTable({
    list,
    totalItems,
    rowsPerPage,
    page,
    onChange,
    isFetching,
    isLoading,
}: ICouponsTable) {
    const navigate = useNavigate();
    const editAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.UPDATE);
    const deleteAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.DELETE);
    const revokeAccess = useCheckPermissionAccess(PagesAccess.COUPON, OperationAccess.DELETE);

    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof ICoupon>("updatedAt");
    const [confirmModalId, setConfirmModal] = useState<number | null>(null);
    const [revokeModalId, setRevokeModal] = useState<number | null>(null);
    const [toggleModalId, setToggleModal] = useState<number | null>(null);

    const { mutate: deleteCoupon, isPending } = useDeleteCoupon();
    const { mutate: revokeCoupon, isPending: isRevoking } = useRevokeCoupon();
    const { mutate: toggleCoupon, isPending: isToggling } = useToggleCoupon();

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string | number | symbol) => {
        const isAsc = orderBy === property && order === "asc";
        const newOrder = isAsc ? "desc" : "asc";
        setOrder(newOrder);
        setOrderBy(property as keyof ICoupon);

        // Call API with sort parameter - format: "field:order" (same as customer API)
        const sortParam = `${String(property)}:${newOrder}`;
        onChange({ sort: sortParam });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        onChange({ page: newPage, rowsPerPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
    };

    const handleEdit = (id: number) => navigate(ROUTE_CONSTANTS.SETTINGS.COUPONS.UPDATE.EDIT_BY_ID(id).ABSOLUTE);
    const handleDetail = (id: number) => navigate(ROUTE_CONSTANTS.SETTINGS.COUPONS.SHOW.SHOW_BY_ID(id).ABSOLUTE);
    const handleClose = () => setConfirmModal(null);
    const handleDelete = () =>
        confirmModalId &&
        deleteCoupon(confirmModalId, {
            onSuccess: () => {
                setConfirmModal(null);
            },
        });

    const handleRevoke = (revokeReason?: string) => {
        if (!revokeModalId) return;

        revokeCoupon(
            {
                id: revokeModalId,
                body: { revokeReason },
            },
            {
                onSuccess: () => {
                    setRevokeModal(null);
                    enqueueSnackbar("Coupon revoked successfully", { variant: "success" });
                },
                onError: (err: any) => {
                    const error = getAxiosError(err);
                    const message = error?.meta.message || "Failed to revoke coupon";
                    enqueueSnackbar(message, { variant: "error" });
                },
            }
        );
    };

    const handleToggle = () => {
        if (!toggleModalId) return;

        toggleCoupon(toggleModalId, {
            onSuccess: () => {
                setToggleModal(null);
                enqueueSnackbar("Coupon status toggled successfully", { variant: "success" });
            },
            onError: (err: any) => {
                const error = getAxiosError(err);
                const message = error?.meta.message || "Failed to toggle coupon status";
                enqueueSnackbar(message, { variant: "error" });
            },
        });
    };

    const getAmountTypeLabel = (amountType: string) => {
        return amountType === "FIXED" ? "Fixed" : "Percentage";
    };

    const getAppliedToLabel = (appliedTo: string) => {
        return appliedTo === "ALL" ? "All" : "First Subscription";
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return "success";
            case "DE_ACTIVE":
                return "warning";
            case "REVOKED":
                return "error";
            case "MAXED_OUT":
                return "error";
            default:
                return "default";
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case "ACTIVE":
                return "Active";
            case "DE_ACTIVE":
                return "Deactivated";
            case "REVOKED":
                return "Revoked";
            case "MAXED_OUT":
                return "Maxed Out";
            default:
                return status;
        }
    };

    const visibleRows = useMemo(() => list, [list]);

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
            <TableContainer sx={{ maxHeight: "700px" }}>
                <Table aria-labelledby="tableTitle" className="table" component="div" size="medium" sx={{ padding: ".5em" }}>
                    <EnhancedTableHead<any>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={list.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div">
                        {isLoading && <TableSkeleton cells={headCells.map((item) => String(item.id))} />}
                        {!isLoading && !isFetching && visibleRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length}>No Data To Show</TableCell>
                            </TableRow>
                        )}
                        {isFetching && <Loading />}
                        {visibleRows.map((coupon: ICoupon) => {
                            const { id, title, amountType, amountValue, appliedTo, status, expireDate, usageLimit } = coupon;

                            return (
                                <TableRow key={id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{title || "No Title"}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{getAmountTypeLabel(amountType)}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            {amountValue}
                                            {amountType === "FIXED" ? " USD" : "%"}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{getAppliedToLabel(appliedTo)}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value fit">
                                            <Chip
                                                color={getStatusColor(status)}
                                                label={getStatusLabel(status)}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            {expireDate ? dayjs(expireDate).format("YYYY-MM-DD HH:mm") : "No Expiry"}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{usageLimit || "Unlimited"}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{coupon.usageCount || 0}</span>
                                    </TableCell>
                                    <TableCell align="right" className="table-cell" component="div">
                                        <span className="table-value" style={{ justifyContent: "flex-end", gap: "0.5em" }}>
                                            <IconButton onClick={() => handleDetail(id)} size="small" title="View Details">
                                                <Visibility />
                                            </IconButton>
                                            {editAccess &&
                                                coupon.canUpdate &&
                                                status !== "REVOKED" &&
                                                status !== "MAXED_OUT" && (
                                                    <IconButton
                                                        onClick={() => handleEdit(id)}
                                                        size="small"
                                                        title="Edit Coupon"
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                )}
                                            {revokeAccess && status !== "REVOKED" && status !== "MAXED_OUT" && (
                                                <IconButton
                                                    onClick={() => setRevokeModal(id)}
                                                    size="small"
                                                    title="Revoke Coupon"
                                                    color="error"
                                                >
                                                    <Block />
                                                </IconButton>
                                            )}
                                            {editAccess && status !== "REVOKED" && status !== "MAXED_OUT" && (
                                                <IconButton
                                                    onClick={() => setToggleModal(id)}
                                                    size="small"
                                                    title={`${status === "ACTIVE" ? "Deactivate" : "Activate"} Coupon`}
                                                    color={status === "ACTIVE" ? "warning" : "success"}
                                                >
                                                    <PowerSettingsNew />
                                                </IconButton>
                                            )}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <ConfirmationModal
                open={!!confirmModalId}
                handleClose={handleClose}
                onConfirm={handleDelete}
                title="Delete Coupon"
                description="Are you sure you want to delete this coupon? This action cannot be undone."
                loading={isPending}
            />

            <RevokeModal
                open={!!revokeModalId}
                onClose={() => setRevokeModal(null)}
                onConfirm={handleRevoke}
                loading={isRevoking}
            />

            <ConfirmationModal
                open={!!toggleModalId}
                handleClose={() => setToggleModal(null)}
                onConfirm={handleToggle}
                title="Toggle Coupon Status"
                description={`Are you sure you want to ${toggleModalId && list.find((c) => c.id === toggleModalId)?.status === "ACTIVE" ? "deactivate" : "activate"} this coupon?`}
                loading={isToggling}
            />
        </TableWrapperStyle>
    );
}
